import { isEmpty } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  DO_NOT_SERIALIZE,
  FilterItem,
  FilterItemSource,
  IFilterViewModel,
} from '../../FiltersViewModel'
import { task } from 'mobx-task/lib/task'

export class SingleSelectFilter<TKey, TValue>
  implements IFilterViewModel<TKey, TValue>
{
  @observable editing = false
  @observable selected: TValue
  @observable options: FilterItem<TValue>[]
  @observable searchText = ''

  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  constructor(
    public title: string,
    public key: TKey,
    private initialValue: TValue,
    public itemSource: FilterItemSource<TValue>,
    private defaultToFirstOption = true,
  ) {
    this.key = key
    this.options = []
    this.itemSource = itemSource
    this.selected = initialValue
    makeObservable(this)
  }

  loadOptions = task(async () => {
    if (this.itemSource) {
      this.setOptions(await this.itemSource())
    }
  })

  async activate() {
    await this.loadOptions()

    if (this.defaultToFirstOption) {
      this.change(this.options[0].value)
    }
  }

  async refresh() {
    await this.loadOptions()
  }

  toString() {
    if (!this.selected) {
      return ''
    }
    return this.optionsMap.get(this.selected) ?? ''
  }

  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  @computed
  get value() {
    return this.selected
  }

  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  @computed
  get searchFilterOptions() {
    if (this.searchText.length === 0) {
      return this.options.slice()
    }

    return this.options.filter(
      (option) =>
        option.title.toLowerCase().includes(this.searchText) ||
        option.description?.toLowerCase().includes(this.searchText),
    )
  }

  @action.bound
  changeSearchText(text: string) {
    this.searchText = text
  }

  @action.bound
  change(val: TValue) {
    this.selected = val
  }

  @action.bound
  clearValue() {
    if (this.defaultToFirstOption) {
      this.selected = this.options[0].value
    }
    this.selected = this.initialValue
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  isSelected(val: TValue) {
    return this.selected === val
  }

  serialize() {
    return JSON.stringify(this.selected) || DO_NOT_SERIALIZE
  }

  deserialize(value: unknown) {
    /**
     * May behave unexpectedly if existing enum values change
     */
    try {
      if (typeof value === 'string') {
        this.selected = JSON.parse(value || '{}')
      }
    } catch (error) {
      this.clearValue()
    }
  }
}
